import { useState, useEffect, useRef } from "react";
import type { Map as MapLibreMap } from "maplibre-gl";

/**
 * AttributionPill — region-keyed data-source credit.
 *
 * Renders the legally-required attribution for whichever alert data is ACTUALLY
 * drawn in the current viewport, not whatever the map is centered on. It reads
 * the `_source` property the alert normalizers already stamp on every feature
 * ("nws" for US, "eccc" for Canada) by querying the rendered features in the
 * map view, so the credit always matches the pixels on screen.
 *
 * WHY TEXT, NOT LOGOS — this is the whole reason the pill exists:
 *   • US  (NOAA/NWS): public-domain federal work. A logo would be permissible,
 *     but we keep a uniform text treatment for consistency across regions.
 *   • CA  (ECCC): the MSC Open Data End-use Licence grants broad commercial use
 *     BUT explicitly withholds any right to use ECCC "names, crests, logos, or
 *     other official symbols," and bars any impression of official endorsement.
 *     So Canada MUST be a text credit — never the Environment Canada roundel.
 *     The licence also REQUIRES attribution (unlike the US public-domain data,
 *     where it's courtesy), and non-compliance terminates the licence grant.
 *
 * Europe (MeteoAlarm/EUMETNET) is intentionally NOT wired yet — that feed isn't
 * built, and its logo/branding terms need their own review before anything for
 * that region renders. The SOURCE_CREDITS map has the hook; add the entry when
 * the data path and its licence are confirmed.
 *
 * Behavior when the view is over open ocean / outside every region: no source
 * features are rendered, so `sources` is empty and the pill hides entirely.
 */

// ── Region credit registry ──────────────────────────────────────────────────
// Keyed by the `_source` tag the normalizers set. `text` is the exact wording
// the provider's licence accepts; `href` deep-links to that licence so the pill
// can satisfy the "where possible, provide a link to this licence" clause.
//
// Order here is the display priority when MULTIPLE sources are visible at once
// (e.g. a view straddling the US/Canada border shows both, joined). Listed
// US-first purely for left-to-right reading; neither provider requires primacy.
interface SourceCredit {
  /** Licence-approved attribution string. ECCC's is mandated almost verbatim. */
  text: string;
  /** Link to the governing licence (opens in a new tab). */
  href: string;
  /** Accessible expansion of the short credit, for screen readers / title. */
  full: string;
}

const SOURCE_CREDITS: Record<string, SourceCredit> = {
  nws: {
    text: "NOAA",
    href: "https://www.weather.gov/disclaimer",
    full: "US alert data: NOAA National Weather Service (public domain)",
  },
  eccc: {
    // MSC Open Data End-use Licence v2.1, §3: the accepted attribution form.
    // Kept as the full official name on purpose — do not shorten to just
    // "Environment Canada," and do not swap in a logo.
    text: "Environment and Climate Change Canada",
    href: "https://eccc-msc.github.io/open-data/licence/readme_en/",
    full:
      "Contains information licenced under the Data Server End-use Licence " +
      "of Environment and Climate Change Canada",
  },
  // Tropical cyclone overlay — the Esri Master License Agreement requires
  // "Powered by Esri" attribution when using Esri services or data. The
  // underlying NHC/JTWC forecast data is public-domain federal work, but
  // Esri's aggregation and hosting layer carries its own attribution requirement.
  esri_tropical: {
    text: "Powered by Esri",
    href: "https://developers.arcgis.com/documentation/esri-and-data-attribution/",
    full: "Tropical cyclone data: NOAA NHC & JTWC, aggregated by Esri Living Atlas",
  },
  // SPC Day 1 convective outlooks — public-domain NOAA data, but the credit
  // is more specific than the blanket "NOAA" used for NWS alerts, so users
  // know exactly which product they're seeing.
  spc: {
    text: "SPC",
    href: "https://www.spc.noaa.gov/",
    full: "Convective outlook data: NOAA Storm Prediction Center (public domain)",
  },
  // meteoalarm: { … }  ← add only after the EUMETNET data path + logo/branding
  //                       terms are reviewed. Europe stays unattributed (and
  //                       unshipped) until then.
};

// Stable display order regardless of which order the sources were discovered.
const SOURCE_ORDER = ["nws", "eccc", "spc", "esri_tropical"];

// ── Hook: which alert sources are rendered in the current viewport ───────────
// Returns the SET of `_source` values present among features actually rendered
// in the alert layer right now. Empty when alerts are off, nothing's in view,
// or the view is over open water. Recomputed on moveend (pan/zoom settle) and
// whenever the underlying data version changes (new fetch merged in).
//
// `queryRenderedFeatures` only returns features in the current viewport, which
// is exactly the semantic we want: the credit tracks what's on screen. We scope
// the query to the alert fill layer so basemap/other overlays never leak in.
export function useVisibleAlertSources(
  map: MapLibreMap | null,
  opts: {
    /** The alert fill layer id to query (e.g. "alerts-fill"). */
    layerId: string;
    /** Whether the alert overlay is currently on. When false → always empty. */
    enabled: boolean;
    /** Bump this when alert data changes so the set re-evaluates after a fetch. */
    dataVersion?: unknown;
  }
): Set<string> {
  const { layerId, enabled, dataVersion } = opts;
  const [sources, setSources] = useState<Set<string>>(() => new Set());
  // Hold the latest set in a ref so the equality check inside the listener
  // doesn't need `sources` in its dependency array (which would re-bind the
  // listener on every change).
  const sourcesRef = useRef(sources);
  sourcesRef.current = sources;

  useEffect(() => {
    if (!map || !enabled) {
      // Clear when disabled so a stale credit can't linger after toggle-off.
      if (sourcesRef.current.size > 0) setSources(new Set());
      return;
    }

    const recompute = () => {
      // The layer may not exist yet on the first tick after enabling (the
      // render effect adds it asynchronously). Guard so the query can't throw.
      if (!map.getLayer(layerId)) {
        if (sourcesRef.current.size > 0) setSources(new Set());
        return;
      }

      let next: Set<string>;
      try {
        const feats = map.queryRenderedFeatures({ layers: [layerId] });
        next = new Set<string>();
        for (const f of feats) {
          const s = (f.properties as any)?._source;
          // Only count sources we have a credit for. An unknown/missing tag is
          // skipped rather than shown as a blank credit.
          if (typeof s === "string" && SOURCE_CREDITS[s]) next.add(s);
        }
      } catch {
        next = new Set();
      }

      // Shallow set-equality: skip the state update (and re-render) when the
      // visible-source set is unchanged, which is the common case while panning
      // within one country.
      const prev = sourcesRef.current;
      if (prev.size === next.size && [...next].every((s) => prev.has(s))) return;
      setSources(next);
    };

    // moveend fires after both pans and zooms settle. We also run once now so
    // the pill is correct on mount / immediately after data arrives.
    recompute();
    map.on("moveend", recompute);
    // sourcedata fires as tiles/features finish loading — catches the case where
    // features stream in without a camera move (e.g. first paint after a fetch).
    map.on("sourcedata", recompute);

    return () => {
      map.off("moveend", recompute);
      map.off("sourcedata", recompute);
    };
    // dataVersion in deps so a merged-in fetch re-evaluates even without a move.
  }, [map, layerId, enabled, dataVersion]);

  return sources;
}

// ── Component ────────────────────────────────────────────────────────────────
export interface AttributionPillProps {
  /** The set of visible `_source` tags from useVisibleAlertSources. */
  sources: Set<string>;
  /**
   * Distance from the bottom of the map to the pill, in px. Used as a FALLBACK
   * when `anchorSelector` is not supplied or its element isn't found. When the
   * anchor IS found, the pill is positioned 10px above that element's top edge
   * instead, so it self-corrects as the timeline pill grows/shrinks.
   */
  bottom?: number;
  /**
   * CSS selector for the element to sit above (e.g. the timeline pill). When
   * provided and found, the pill is placed `gap` px above that element's top,
   * measured from the live layout — robust to the timeline pill changing height
   * (preload bar showing/hiding, legend swatches, mobile reflow). The element
   * is re-measured on resize and whenever `sources` changes.
   */
  anchorSelector?: string;
  /** Gap in px between the anchor's top edge and this pill's bottom. */
  gap?: number;
  /** Small-viewport flag so sizing matches the rest of your mobile pills. */
  isSmall?: boolean;
}

export function AttributionPill({
  sources,
  bottom = 88,
  anchorSelector,
  gap = 10,
  isSmall = false,
}: AttributionPillProps) {
  // Measured bottom offset (px from viewport bottom) when anchoring to another
  // element. null → not measured yet / no anchor → use the `bottom` fallback.
  const [measuredBottom, setMeasuredBottom] = useState<number | null>(null);

  useEffect(() => {
    if (!anchorSelector) {
      setMeasuredBottom(null);
      return;
    }
    // The element currently being observed for size changes. Tracked so the
    // MutationObserver below can tell "same anchor, some unrelated DOM churn"
    // (do nothing) apart from "the anchor mounted / unmounted / was replaced"
    // (re-bind + re-measure).
    let observedEl: HTMLElement | null = null;
    let ro: ResizeObserver | null = null;

    const measure = () => {
      const el = document.querySelector(anchorSelector) as HTMLElement | null;
      if (!el) {
        setMeasuredBottom(null); // fall back to `bottom` until the anchor exists
        return;
      }
      const rect = el.getBoundingClientRect();
      // Distance from the viewport bottom up to the anchor's TOP edge, plus the
      // gap — this is the CSS `bottom` that lands the pill `gap` px above it.
      const fromViewportBottom = window.innerHeight - rect.top + gap;
      setMeasuredBottom(fromViewportBottom);
    };

    // (Re)attach the ResizeObserver to whatever the anchor currently resolves
    // to, then measure. Safe to call repeatedly — it's a no-op unless the
    // resolved element's identity changed.
    const bind = () => {
      const el = document.querySelector(anchorSelector) as HTMLElement | null;
      if (el !== observedEl) {
        ro?.disconnect();
        observedEl = el;
        if (el && "ResizeObserver" in window) {
          ro = new ResizeObserver(measure);
          ro.observe(el);
        }
        measure();
      }
    };

    // Measure now, after layout settles (rAF), on resize, and whenever the
    // timeline pill might change size. The ResizeObserver catches in-place
    // growth (preload bar, legend rows appearing) without polling.
    //
    // THE LATE-MOUNT CASE — the reason for the MutationObserver: the timeline
    // pill only mounts while a raster layer is active. If this pill renders
    // first (e.g. hurricanes visible, no layer yet), querySelector finds
    // nothing, we sit at the `bottom` fallback, and there is no element to
    // size-observe. When the user then picks Wind, the timeline pill mounts
    // WITH its legend — taller than the fallback allows for — and nothing in
    // this effect's deps changes, so without the MO we'd never re-measure and
    // we'd overlap the legend. The MO watches the DOM for the anchor
    // appearing / vanishing / being remounted (React can replace the node on
    // layer switches) and re-binds. The `el !== observedEl` identity guard in
    // bind() keeps unrelated DOM churn (popups, METAR pills) free: those
    // mutations resolve to the same anchor and return immediately.
    bind();
    const raf = requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    let mo: MutationObserver | null = null;
    if ("MutationObserver" in window) {
      mo = new MutationObserver(bind);
      mo.observe(document.body, { childList: true, subtree: true });
    }
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", measure);
      ro?.disconnect();
      mo?.disconnect();
    };
    // Re-run when sources change (pill mounts/unmounts) or anchor/gap change.
  }, [anchorSelector, gap, isSmall, sources]);

  // Empty set → over ocean / alerts off / nothing in view → render nothing.
  if (sources.size === 0) return null;

  const visible = SOURCE_ORDER.filter((s) => sources.has(s));
  if (visible.length === 0) return null;

  const effectiveBottom = measuredBottom ?? bottom;

  // The pill sits centered horizontally like the legend/timeline pills. It is a
  // CREDIT, so it's deliberately quieter than the interactive pills: lower text
  // opacity, no pointer affordance on the body, links underlined on hover only.
  return (
    <div
      // Tagged so the map's ⓘ attribution button can measure this pill as an
      // obstacle and lift itself clear of it (see keepInfoButtonClear in
      // app.tsx). Without the tag the ⓘ would only dodge the timeline pill and
      // could still land on top of this credit line.
      data-attrib-pill
      style={{
        position: "absolute",
        bottom: effectiveBottom,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 55, // below interactive pills (60) so it never blocks a control
        display: "flex",
        alignItems: "center",
        gap: 5,
        maxWidth: isSmall ? "calc(100vw - 24px)" : "min(92vw, 640px)",
        padding: isSmall ? "5px 11px" : "6px 13px",
        background: "rgba(8,10,16,0.82)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: 999,
        boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
        fontFamily: "Roboto, -apple-system, system-ui, sans-serif",
        fontSize: isSmall ? 9.5 : 10.5,
        lineHeight: 1.2,
        letterSpacing: 0.3,
        color: "rgba(226,229,237,0.62)", // quieter than control text (#e2e5ed)
        whiteSpace: "nowrap",
        overflow: "hidden",
        pointerEvents: "auto",
      }}
    >
      {/* Single "Data:" label, then the in-view source names joined by commas.
          One source → "Data: NOAA". Border view → "Data: NOAA, Environment and
          Climate Change Canada". The comma is rendered as a direct sibling of
          the link (no flex gap between them) so it hugs the preceding name. */}
      <span aria-hidden style={{ flexShrink: 0 }}>Data:</span>
      {visible.map((s, i) => {
        const c = SOURCE_CREDITS[s];
        const isLast = i === visible.length - 1;
        return (
          <span key={s} style={{ display: "inline-flex", alignItems: "baseline", minWidth: 0 }}>
            <a
              href={c.href}
              target="_blank"
              rel="noopener noreferrer"
              title={c.full}
              style={{
                color: "inherit",
                textDecoration: "none",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
              onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
            >
              {c.text}
            </a>
            {!isLast && <span aria-hidden>,</span>}
          </span>
        );
      })}
    </div>
  );
}